// /components/OekobaudatExplorer.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Loader2, Search, Database, Info, ArrowRightLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import MaterialProperties from './MaterialProperties';
import {
  fetchLatestDatastock,
  searchProcesses,
  loadProcessDetails,
  formatModules,
  enrichProcessNames
} from '../services/oekobaudatService';

// --- EN-ONLY gating: don't show list titles until we have an English name ---
const STRICT_EN_NAMES = true;  // set false to relax

const SAMPLE_QUERIES = ['concrete', 'timber', 'insulation', 'steel', 'gypsum'];
const RESULTS_PAGE_SIZE = 40;
const MIN_QUERY_LEN = 2;

function hasEnglishName(item) {
  return typeof item?.displayName === 'string' && item.displayName.trim().length > 0;
}

function fillEnglishFromMap(items, nameMap) {
  return items.map((item) => {
    const english = nameMap?.get?.(item.uuid);
    return {
      ...item,
      displayName: (typeof english === 'string' ? english : (item.displayName || '')).trim(),
      fallbackName: (item.fallbackName || item.name || '').trim()
    };
  });
}

function formatSelectionSummary(values, { fallback = 'All', transform } = {}) {
  if (!Array.isArray(values) || values.length === 0) return fallback;
  const mapped = transform ? values.map(transform) : values.slice();
  if (mapped.length <= 2) return mapped.join(', ');
  return `${mapped.slice(0, 2).join(', ')} +${mapped.length - 2}`;
}

function useDebouncedValue(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => { const h = setTimeout(() => setDebounced(value), delay); return () => clearTimeout(h); }, [value, delay]);
  return debounced;
}

function formatImpactValue(value) {
  if (Number.isNaN(value)) return '-';
  const absValue = Math.abs(value);
  if (absValue >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (absValue >= 1) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (absValue === 0) return '0';
  if (absValue < 0.001) return value.toExponential(2);
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function normalizeUnitLabel(raw) {
  if (!raw) return raw;
  let s = String(raw);
  s = s.replace(/\u00c2/g, '');
  s = s.replace(/CO_?\(?2\)?/gi, 'CO2');
  s = s.replace(/m3/g, 'm³');
  s = s.replace(/m2/g, 'm²');
  s = s.replace(/\u00b3/g, '³');
  s = s.replace(/\u00b2/g, '²');
  s = s.replace(/CO_?\(?2\)?-\u00c4q\.?/gi, 'CO2-eq');
  s = s.replace(/CO_\(?2\)?-\u00c4q\.?/gi, 'CO2-eq');
  s = s.replace(/CO2-\u00c4q\.?/gi, 'CO2-eq');
  s = s.replace(/CO2-\u00c4qv\.?/gi, 'CO2-eq');
  s = s.replace(/CO2-\u017dqv\.?/gi, 'CO2-eq');
  s = s.replace(/CO2-?eqv\.?/gi, 'CO2-eq');
  s = s.replace(/CO2\s*eq\.?/gi, 'CO2-eq');
  return s;
}

function formatQuantity(value) {
  if (!Number.isFinite(value)) return null;
  const absValue = Math.abs(value);
  if (absValue >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (absValue >= 1) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (absValue === 0) return '0';
  if (absValue < 0.001) return value.toExponential(2);
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}
function formatDeclaredUnit(declaredUnit) {
  if (!declaredUnit) return null;
  const unit = normalizeUnitLabel(declaredUnit.unit || 'declared unit');
  const amount = Number.isFinite(Number(declaredUnit.amount)) ? Number(declaredUnit.amount) : null;
  if (amount != null) {
    const formattedAmount = formatQuantity(amount) || String(amount);
    return `${formattedAmount} ${unit}`.trim();
  }
  return `1 ${unit}`.trim();
}

// --- validUntil helpers (unchanged) ---
function normalizeValidUntilCandidate(candidate) {
  if (candidate == null) return null;
  if (typeof candidate === 'string' || typeof candidate === 'number') {
    const str = String(candidate).trim();
    return str.length ? str : null;
  }
  if (Array.isArray(candidate)) {
    for (const entry of candidate) {
      const resolved = normalizeValidUntilCandidate(entry);
      if (resolved) return resolved;
    }
    return null;
  }
  if (typeof candidate === 'object') {
    const probeKeys = ['value','text','label','date','endDate','end','year','Year','time','validTo','validUntil'];
    for (const key of probeKeys) {
      if (candidate[key] != null) {
        const resolved = normalizeValidUntilCandidate(candidate[key]);
        if (resolved) return resolved;
      }
      const attrKey = `@${key}`;
      if (candidate[attrKey] != null) {
        const resolved = normalizeValidUntilCandidate(candidate[attrKey]);
        if (resolved) return resolved;
      }
    }
  }
  return null;
}
function extractValidUntil(detail, fallbackProcess) {
  const candidates = [
    detail?.validUntil, detail?.validTo, detail?.processValidity,
    detail?.processInformation?.dataSetInformation?.validUntil,
    detail?.processInformation?.dataSetInformation?.validTo,
    detail?.processInformation?.dataSetInformation?.timePeriod?.endDate,
    detail?.processInformation?.dataSetInformation?.timePeriod?.end,
    detail?.processInformation?.timePeriod?.endDate,
    detail?.processInformation?.timePeriod?.end,
    detail?.documentation?.validUntil,
    fallbackProcess?.validUntil, fallbackProcess?.validTo
  ];
  for (const c of candidates) {
    const normalized = normalizeValidUntilCandidate(c);
    if (normalized) return normalized;
  }
  return null;
}

// --- Impact table ---
const ImpactCategoryTable = ({ impact, declaredUnit }) => {
  const modules = formatModules(impact.modules);
  const perUnitTotal = impact.total;
  const declaredUnitLabel = formatDeclaredUnit(declaredUnit) || 'declared unit';
  return (
    <Card className="shadow-sm border-emerald-100/80 bg-white">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base text-emerald-700 flex items-center gap-2">
            <span>{impact.description}</span>
            {impact.unit && (
              <Badge variant="secondary" className="text-xs bg-emerald-50 text-emerald-700">
                {normalizeUnitLabel(impact.unit)} / {declaredUnitLabel}
              </Badge>
            )}
          </CardTitle>
          <Badge className="bg-emerald-600 text-white text-xs">
            {formatImpactValue(perUnitTotal)} {normalizeUnitLabel(impact.unit || '')} / {normalizeUnitLabel(declaredUnitLabel)}
          </Badge>
        </div>
        <CardDescription className="text-xs">Impacts per {declaredUnitLabel}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-lg border border-emerald-100/70">
          <table className="w-full text-sm">
            <thead className="bg-emerald-50 text-emerald-700">
              <tr>
                <th className="text-left font-medium px-3 py-2">Module</th>
                <th className="text-right font-medium px-3 py-2">Impact / {declaredUnitLabel}</th>
              </tr>
            </thead>
            <tbody>
              {modules.map(({ key, value }) => (
                <tr key={key} className="odd:bg-white even:bg-emerald-50/40">
                  <td className="px-3 py-2 font-medium text-emerald-800">{key}</td>
                  <td className="px-3 py-2 text-right text-emerald-900">{formatImpactValue(value)}</td>
                </tr>
              ))}
              <tr className="bg-emerald-100/60 text-emerald-900 font-semibold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{formatImpactValue(perUnitTotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// --- Cards ---
const ProcessResultItem = ({ process, isSelected, onSelect, showTitleSkeleton = false }) => {
  const location = process.geo || '-';
  const classification = process.classific || process.type || '';
  const displayName = typeof process.displayName === 'string' ? process.displayName.trim() : '';
  const fallbackName = typeof process.fallbackName === 'string'
    ? process.fallbackName.trim()
    : (typeof process.name === 'string' ? process.name.trim() : '');
  const title = displayName || fallbackName || 'Unnamed dataset';

  return (
    <button
      type="button"
      onClick={() => onSelect(process)}
      className={`w-full text-left p-4 rounded-xl border transition shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500/60 flex flex-col gap-1 ${isSelected ? 'border-emerald-500 bg-emerald-50/70' : 'border-emerald-100 bg-white/80'}`}
    >
      <div className="flex items-center justify-between gap-3">
        {showTitleSkeleton ? (
          <span className="inline-block w-3/4 h-4 rounded bg-emerald-100/70 animate-pulse" />
        ) : (
          <span className="font-semibold text-gray-800 line-clamp-2">{title}</span>
        )}
        <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[11px]">
          {process.version || 'N/A'}
        </Badge>
      </div>
      <div className="text-xs text-gray-600 flex flex-wrap gap-2">
        <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">{classification}</Badge>
        {process.subtype && (
          <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">{process.subtype}</Badge>
        )}
        <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">{location}</Badge>
      </div>
    </button>
  );
};

const SkeletonProcessItem = () => (
  <div className="w-full p-4 rounded-xl border border-emerald-100 bg-white/80 shadow-sm">
    <div className="animate-pulse space-y-3">
      <div className="h-4 bg-emerald-100/70 rounded w-3/4" />
      <div className="flex gap-2">
        <div className="h-5 bg-emerald-50 rounded w-20" />
        <div className="h-5 bg-emerald-50 rounded w-24" />
        <div className="h-5 bg-emerald-50 rounded w-16" />
      </div>
    </div>
  </div>
);

const OekobaudatExplorer = ({ onShortlist, shortlist = [], onStatusChange, onRegisterRefresh }) => {
  const inputRef = useRef(null);
  const [datastock, setDatastock] = useState(null);
  const [datastockStatus, setDatastockStatus] = useState('loading');
  const [datastockError, setDatastockError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 350);

  const [pages, setPages] = useState(new Map());
  const [pageReady, setPageReady] = useState(new Map()); // pageIndex -> boolean (true means EN-checked)
  const [searchStatus, setSearchStatus] = useState('idle');
  const [searchError, setSearchError] = useState(null);

  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectedSubtypes, setSelectedSubtypes] = useState([]);
  const [selectedGeographies, setSelectedGeographies] = useState([]);
  const inFlightPagesRef = useRef(new Set());

  const allResults = useMemo(() => {
    const entries = Array.from(pages.entries()).sort((a, b) => a[0] - b[0]);
    return entries.flatMap(([, items]) => items);
  }, [pages]);

  const availableLanguages = useMemo(() => {
    const langs = new Set(['en', 'de']);
    allResults.forEach((process) => {
      (process.languages || []).forEach((lang) => {
        if (typeof lang === 'string') langs.add(lang.toLowerCase());
      });
    });
    return Array.from(langs).sort();
  }, [allResults]);

  const availableSubtypes = [
    'Generic dataset','Representative dataset','Average dataset','Specific dataset','Template dataset'
  ];

  const availableGeographies = useMemo(() => {
    const map = new Map();
    allResults.forEach((process) => {
      const candidates = [
        process.geography,
        process.geo,
        process.location,
        process?.processInformation?.geography?.locationOfOperationSupplyOrProduction?.location,
        process?.locationOfOperationSupplyOrProduction,
        process?.country,
        process?.countryCode
      ];
      candidates.forEach((candidate) => {
        if (!candidate) return;
        let value = null;
        if (typeof candidate === 'string') {
          value = candidate.trim();
        } else if (typeof candidate === 'object') {
          value = candidate.location || candidate.name || candidate.code || candidate.id;
          if (Array.isArray(candidate)) {
            candidate.forEach((entry) => {
              if (typeof entry === 'string' && entry.trim()) {
                const key = entry.trim().toLowerCase();
                if (!map.has(key)) map.set(key, entry.trim());
              }
            });
            value = null;
          }
          if (value && typeof value === 'object') value = value.value || value.text;
          if (value && typeof value === 'string') value = value.trim();
        }
        if (value && typeof value === 'string' && value.length > 0) {
          const key = value.toLowerCase();
          if (!map.has(key)) map.set(key, value);
        }
      });
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b));
  }, [allResults]);

  const [searchPageInfo, setSearchPageInfo] = useState({ totalCount: 0, pageSize: RESULTS_PAGE_SIZE });
  const [currentPage, setCurrentPage] = useState(0);

  const hasLanguageFilter = selectedLanguages.length > 0;
  const hasSubtypeFilter = selectedSubtypes.length > 0;
  const hasGeographyFilter = selectedGeographies.length > 0;

  const filteredResults = useMemo(() => {
    let results = allResults;

    if (selectedLanguages.length > 0) {
      results = results.filter((process) => {
        const langs = Array.isArray(process.languages)
          ? process.languages.map((lang) => (typeof lang === 'string' ? lang.toLowerCase() : null)).filter(Boolean)
          : [];
        if (langs.length === 0) return true;
        return langs.some((lang) => selectedLanguages.includes(lang));
      });
    }

    if (selectedSubtypes.length > 0) {
      const wanted = selectedSubtypes.map((s) => s.toLowerCase());
      results = results.filter((process) => {
        const subtype = (process.subtype || (process.documentation && process.documentation.methodologyNotes) || '')
          .toString()
          .toLowerCase();
        return wanted.some((w) => subtype.includes(w));
      });
    }

    if (selectedGeographies.length > 0) {
      const wanted = selectedGeographies.map((geo) => geo.toLowerCase());
      results = results.filter((process) => {
        const candidateValues = [
          process.geography,
          process.geo,
          process.location,
          process?.processInformation?.geography?.locationOfOperationSupplyOrProduction?.location,
          process?.processInformation?.geography?.locationOfOperationSupplyOrProduction?.name,
          process?.country,
          process?.countryCode
        ];

        const normalized = candidateValues
          .flatMap((value) => {
            if (!value) return [];
            if (Array.isArray(value)) return value.filter((i) => typeof i === 'string').map((i) => i.toLowerCase());
            if (typeof value === 'string') return [value.toLowerCase()];
            if (typeof value === 'object') {
              const possible = [value.location, value.name, value.code, value.id, value.value, value.text]
                .filter((entry) => typeof entry === 'string' && entry.trim().length > 0)
                .map((entry) => entry.toLowerCase());
              return possible;
            }
            return [];
          })
          .filter(Boolean);

        if (normalized.length === 0) return false;
        return normalized.some((candidate) =>
          wanted.some((geo) => candidate.includes(geo) || geo.includes(candidate))
        );
      });
    }

    return results;
  }, [allResults, selectedLanguages, selectedSubtypes, selectedGeographies]);

  const filterEmptyMessage = useMemo(() => {
    const active = [];
    if (hasLanguageFilter) active.push('language');
    if (hasSubtypeFilter) active.push('subtype');
    if (hasGeographyFilter) active.push('country');

    if (active.length > 0) {
      const formatter = typeof Intl !== 'undefined' && Intl.ListFormat
        ? new Intl.ListFormat('en', { style: 'long', type: 'conjunction' })
        : null;
      const filtersText = formatter ? formatter.format(active) : active.join(' and ');
      return `No datasets match the selected ${filtersText} filters. Adjust the filters to see more options.`;
    }
    return 'No datasets match the current filters. Adjust the filters to see more options.';
  }, [hasLanguageFilter, hasSubtypeFilter, hasGeographyFilter]);

  useEffect(() => { setCurrentPage(0); }, [selectedLanguages, selectedSubtypes, selectedGeographies]);

  useEffect(() => {
    const pageSize = searchPageInfo.pageSize || RESULTS_PAGE_SIZE;
    const totalPages = pageSize > 0 ? Math.ceil(filteredResults.length / pageSize) : 0;
    setCurrentPage((prev) => (totalPages === 0 ? 0 : Math.min(prev, totalPages - 1)));
  }, [filteredResults.length, searchPageInfo.pageSize]);

  const handleLanguageToggle = useCallback((lang, nextState) => {
    const normalized = typeof lang === 'string' ? lang.toLowerCase() : lang;
    setSelectedLanguages((prev) => {
      const exists = prev.includes(normalized);
      const shouldAdd = nextState === undefined ? !exists : nextState === true || nextState === 'indeterminate';
      if (shouldAdd) return exists ? prev : [...prev, normalized];
      return prev.filter((item) => item !== normalized);
    });
  }, []);
  const handleSubtypeToggle = useCallback((subtype, nextState) => {
    setSelectedSubtypes((prev) => {
      const exists = prev.includes(subtype);
      const shouldAdd = nextState === undefined ? !exists : nextState === true || nextState === 'indeterminate';
      if (shouldAdd) return exists ? prev : [...prev, subtype];
      return prev.filter((item) => item !== subtype);
    });
  }, []);
  const handleGeographyToggle = useCallback((geography, nextState) => {
    setSelectedGeographies((prev) => {
      const exists = prev.includes(geography);
      const shouldAdd = nextState === undefined ? !exists : nextState === true || nextState === 'indeterminate';
      if (shouldAdd) return exists ? prev : [...prev, geography];
      return prev.filter((item) => item !== geography);
    });
  }, []);

  const clearLanguages = useCallback(() => setSelectedLanguages([]), []);
  const clearSubtypes = useCallback(() => setSelectedSubtypes([]), []);
  const clearGeographies = useCallback(() => setSelectedGeographies([]), []);
  const clearAllFilters = useCallback(() => {
    setSelectedLanguages([]); setSelectedSubtypes([]); setSelectedGeographies([]);
  }, []);

  const fetchPageAndEnrich = useCallback(
    async (pageIndex, signal) => {
      if (!datastock?.uuid || !debouncedSearch || debouncedSearch.trim().length < 2) return null;
      if (inFlightPagesRef.current.has(pageIndex)) return null;

      inFlightPagesRef.current.add(pageIndex);
      setPageReady((prev) => {
        const next = new Map(prev);
        next.set(pageIndex, false);
        return next;
      });

      try {
        const startIndex = pageIndex * RESULTS_PAGE_SIZE;
        const eagerEnrich = true; // we want EN-only gating

        const { data = [], totalCount = 0, pageSize = RESULTS_PAGE_SIZE } = await searchProcesses(
          datastock.uuid,
          debouncedSearch,
          { signal, startIndex, pageSize: RESULTS_PAGE_SIZE, enrich: eagerEnrich }
        );

        setSearchPageInfo({ totalCount, pageSize: pageSize || RESULTS_PAGE_SIZE });

        if (data.length === 0) {
          setPages((prev) => {
            if (prev.has(pageIndex)) return prev;
            const next = new Map(prev);
            next.set(pageIndex, []);
            return next;
          });
          setPageReady((prev) => {
            const next = new Map(prev);
            next.set(pageIndex, true);
            return next;
          });
          return { items: [], totalCount, pageSize: pageSize || RESULTS_PAGE_SIZE };
        }

        // --- Batch enrichment for English names ---
        let enriched = data;
        let pageHasEnglishChecked = false;

        try {
          const uuids = data.map((item) => item.uuid).filter(Boolean);
          if (uuids.length > 0) {
            const primaryMap = await enrichProcessNames(uuids, { signal, concurrency: 8 });
            const nameMap = new Map(primaryMap);

            const missing = data.filter((item) => {
              const value = nameMap.get(item.uuid);
              return !(typeof value === 'string' && value.trim().length > 0);
            });

            if (missing.length > 0) {
              for (const item of missing) {
                if (signal?.aborted) {
                  const abortError = new Error('Aborted');
                  abortError.name = 'AbortError';
                  throw abortError;
                }
                try {
                  const fallbackMap = await enrichProcessNames([item.uuid], { signal, concurrency: 1 });
                  const enrichedName = fallbackMap.get(item.uuid);
                  if (typeof enrichedName === 'string' && enrichedName.trim().length > 0) {
                    nameMap.set(item.uuid, enrichedName.trim());
                  }
                } catch (_) {
                  // ignore per-item errors
                }
              }
            }

            enriched = fillEnglishFromMap(data, nameMap);

            pageHasEnglishChecked = true;
          } else {
            enriched = data.map((item) => ({
              ...item,
              displayName: (item.displayName || '').trim(),
              fallbackName: (item.fallbackName || item.name || '').trim()
            }));
            pageHasEnglishChecked = true;
          }
        } catch (error) {
          if (error?.name === 'AbortError') throw error;
          enriched = data.map((item) => ({
            ...item,
            displayName: (item.displayName || '').trim(),
            fallbackName: (item.fallbackName || item.name || '').trim()
          }));
          pageHasEnglishChecked = true;
        }

        if (STRICT_EN_NAMES) {
          const stillMissing = enriched.filter((it) => !hasEnglishName(it));
          if (stillMissing.length > 0) {
            const results = await Promise.allSettled(
              stillMissing.map((it) =>
                loadProcessDetails(it.uuid, { nameFallback: it.fallbackName, ownerFallback: it.owner })
              )
            );
            const forceNameMap = new Map();
            results.forEach((res, idx) => {
              const target = stillMissing[idx];
              if (res.status === 'fulfilled' && typeof res.value?.name === 'string' && res.value.name.trim()) {
                forceNameMap.set(target.uuid, res.value.name.trim());
              }
            });
            if (forceNameMap.size > 0) {
            enriched = fillEnglishFromMap(enriched, forceNameMap);
            }
          }
        }

        const allHaveEnglish = enriched.every((it) => hasEnglishName(it));
        setPages((prev) => {
          const next = new Map(prev);
          next.set(pageIndex, enriched);
          return next;
        });
        setPageReady((prev) => {
          const next = new Map(prev);
          next.set(pageIndex, STRICT_EN_NAMES ? (pageHasEnglishChecked && allHaveEnglish) : pageHasEnglishChecked);
          return next;
        });

        return { items: enriched, totalCount, pageSize: pageSize || RESULTS_PAGE_SIZE };
      } finally {
        inFlightPagesRef.current.delete(pageIndex);
      }
    },
    [datastock?.uuid, debouncedSearch]
  );

  const [selectedProcess, setSelectedProcess] = useState(null);
  const [processDetail, setProcessDetail] = useState(null);
  const [detailStatus, setDetailStatus] = useState('idle');
  const [detailError, setDetailError] = useState(null);
  const [selectedImpactId, setSelectedImpactId] = useState(null);
  const [isDocumentationDialogOpen, setDocumentationDialogOpen] = useState(false);

  // Helper: patch English name into any page that contains this uuid
  const upsertEnglishNameIntoPages = useCallback((uuid, englishName) => {
    const name = typeof englishName === 'string' ? englishName.trim() : '';
    if (!uuid || !name) return;

    setPages((prev) => {
      let changed = false;
      const next = new Map(prev);

      next.forEach((arr, pageIdx) => {
        if (!Array.isArray(arr) || arr.length === 0) return;
        const index = arr.findIndex((it) => it?.uuid === uuid);
        if (index === -1) return;
        const item = arr[index];
        if (!item?.displayName || item.displayName.trim() !== name) {
          const updatedArr = arr.slice();
          updatedArr[index] = { ...item, displayName: name };
          next.set(pageIdx, updatedArr);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [setPages]);

  const documentationSections = useMemo(() => {
    const doc = processDetail?.documentation;
    if (!doc) return [];
    return [
      { key: 'generalDescription', title: 'General descriptive text', content: doc.generalDescription },
      { key: 'technologyDescription', title: 'Technology and included processes', content: doc.technologyDescription },
      { key: 'useAdvice', title: 'Use advice for dataset', content: doc.useAdvice },
      { key: 'methodologyNotes', title: 'Methodological clarifications', content: doc.methodologyNotes },
      { key: 'reviewDetails', title: 'Review details', content: doc.reviewDetails },
      { key: 'compliance', title: 'Compliance declarations', content: doc.compliance }
    ].filter((s) => typeof s.content === 'string' && s.content.trim().length > 0);
  }, [processDetail?.documentation]);

  const hasDocumentation = documentationSections.length > 0;

  useEffect(() => { setDocumentationDialogOpen(false); }, [processDetail?.uuid]);

  // Reset when search/datastock changes
  useEffect(() => {
    setCurrentPage(0);
    setSearchPageInfo({ totalCount: 0, pageSize: RESULTS_PAGE_SIZE });
    setPages(new Map());
    setPageReady(new Map());
    inFlightPagesRef.current.clear();
  }, [datastock?.uuid, debouncedSearch]);

  // Load datastock
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setDatastockStatus('loading');
        const stock = await fetchLatestDatastock();
        if (!cancelled) { setDatastock(stock); setDatastockStatus('success'); }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to fetch datastock', error);
          setDatastockError(error.message || 'Failed to load datastock metadata.');
          setDatastockStatus('error');
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Kick off search on term
  useEffect(() => {
    if (!datastock?.uuid || !debouncedSearch || debouncedSearch.trim().length < 2) {
      setSearchStatus('idle');
      setSearchError(null);
      setSearchPageInfo({ totalCount: 0, pageSize: RESULTS_PAGE_SIZE });
      setCurrentPage(0);
      setPages(new Map());
      setPageReady(new Map());
      inFlightPagesRef.current.clear();
      return;
    }

    const controller = new AbortController();
    setSearchStatus('loading');
    setSearchError(null);
    setPages(new Map());
    setPageReady(new Map());
    inFlightPagesRef.current.clear();

    (async () => {
      try {
        const firstPage = await fetchPageAndEnrich(0, controller.signal);
        setCurrentPage(0);
        setSearchStatus('success');
        const totalCount = firstPage?.totalCount ?? 0;
        const pageSize = firstPage?.pageSize ?? RESULTS_PAGE_SIZE;
        const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
        if (totalPages > 1) {
          fetchPageAndEnrich(1, controller.signal).catch(() => {});
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('Search failed', error);
        setSearchError(error.message || 'Search failed.');
        setSearchStatus('error');
      }
    })();

    return () => { controller.abort(); };
  }, [datastock?.uuid, debouncedSearch, fetchPageAndEnrich]);

  // Ensure current page is loaded
  useEffect(() => {
    if (!datastock?.uuid || !debouncedSearch || debouncedSearch.trim().length < 2) return;
    if (pages.has(currentPage) || inFlightPagesRef.current.has(currentPage)) return;
    const controller = new AbortController();
    fetchPageAndEnrich(currentPage, controller.signal).catch(() => {});
    return () => controller.abort();
  }, [currentPage, datastock?.uuid, debouncedSearch, pages, fetchPageAndEnrich]);

  // Prefetch next page
  useEffect(() => {
    if (!datastock?.uuid || !debouncedSearch || debouncedSearch.trim().length < 2) return;
    const effectivePageSize = searchPageInfo.pageSize || RESULTS_PAGE_SIZE;
    if (effectivePageSize <= 0) return;

    const totalPages = Math.ceil((searchPageInfo.totalCount || 0) / effectivePageSize);
    const nextPageIndex = currentPage + 1;
    if (nextPageIndex >= totalPages) return;
    if (pages.has(nextPageIndex)) return;

    const controller = new AbortController();
    fetchPageAndEnrich(nextPageIndex, controller.signal).catch(() => {});
    return () => controller.abort();
  }, [currentPage, datastock?.uuid, debouncedSearch, pages, searchPageInfo, fetchPageAndEnrich]);

  // Post-enrichment sweep: any list items with empty `displayName` get a late English lookup
  useEffect(() => {
    if (STRICT_EN_NAMES) {
      setPageReady((prev) => {
        let changed = false;
        const next = new Map(prev);
        pages.forEach((arr, pageIdx) => {
          if (!Array.isArray(arr) || arr.length === 0) return;
          const readyNow = arr.every((item) => hasEnglishName(item));
          if (readyNow && next.get(pageIdx) !== true) {
            next.set(pageIdx, true);
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }

    const unresolvedSet = new Set();
    pages.forEach((arr) => {
      (arr || []).forEach((item) => {
        if (item?.uuid && (!item.displayName || !item.displayName.trim())) {
          unresolvedSet.add(item.uuid);
        }
      });
    });

    if (unresolvedSet.size === 0) return undefined;

    const controller = new AbortController();

    (async () => {
      try {
        const nameMap = await enrichProcessNames(Array.from(unresolvedSet), {
          signal: controller.signal,
          concurrency: 6
        });
        if (!nameMap || typeof nameMap.forEach !== 'function') return;
        nameMap.forEach((maybeEnglish, uuid) => {
          if (typeof maybeEnglish === 'string' && maybeEnglish.trim()) {
            upsertEnglishNameIntoPages(uuid, maybeEnglish);
          }
        });
      } catch (_) {
        // ignore — it's a best-effort background fix
      }
    })();

    return () => controller.abort();
  }, [pages, upsertEnglishNameIntoPages]);

  const handleSelectSample = useCallback((sample) => {
    setSearchTerm(sample);
    setSelectedProcess(null);
    setProcessDetail(null);
    setDetailStatus('idle');
    setDetailError(null);
    requestAnimationFrame(() => { inputRef.current?.focus(); });
  }, []);

  const handleProcessSelect = useCallback(
    async (process) => {
      if (!process?.uuid) return;
      setSelectedProcess(process);
      setProcessDetail(null);
      setDetailStatus('loading');
      setDetailError(null);
      try {
        const fallbackName =
          (typeof process.displayName === 'string' && process.displayName.trim())
            ? process.displayName.trim()
            : (typeof process.fallbackName === 'string' && process.fallbackName.trim())
              ? process.fallbackName.trim()
              : (typeof process.name === 'string' ? process.name : null);

        const detail = await loadProcessDetails(process.uuid, {
          ownerFallback: process.owner,
          nameFallback: fallbackName
        });
        setProcessDetail(detail);
        setDetailStatus('success');

        if (detail?.name) {
          upsertEnglishNameIntoPages(process.uuid, detail.name);
        }

        // Pick a sensible default impact
        try {
          const impacts = Array.isArray(detail?.impacts) ? detail.impacts : [];
          const weights = [
            /gwp[-\s]?total/i,
            /global\s*warming\s*potential.*total/i,
            /climate\s*change\s*-?\s*total/i,
            /climate\s*change/i
          ];
          let best = impacts[0]?.id || null;
          let bestRank = -1;
          impacts.forEach((imp) => {
            const d = String(imp.description || '').toLowerCase();
            for (let i = 0; i < weights.length; i++) {
              if (weights[i].test(d)) {
                const rank = weights.length - i;
                if (rank > bestRank) { bestRank = rank; best = imp.id; }
                break;
              }
            }
          });
          setSelectedImpactId(best);
        } catch (_) {}
      } catch (error) {
        console.error('Failed to load process detail', error);
        setDetailError(error.message || 'Failed to fetch process detail.');
        setDetailStatus('error');
      }
    },
    [upsertEnglishNameIntoPages]
  );

  const validUntilLabel = useMemo(() => extractValidUntil(processDetail, selectedProcess), [processDetail, selectedProcess]);
  const declaredUnitLabel = useMemo(() => formatDeclaredUnit(processDetail?.declaredUnit), [processDetail?.declaredUnit]);
  const datastockSuffix = datastock?.shortName ? ` in ${datastock.shortName}` : '';
  const pageSize = searchPageInfo.pageSize || RESULTS_PAGE_SIZE;

  const isFiltered = selectedLanguages.length > 0 || selectedSubtypes.length > 0 || selectedGeographies.length > 0;

  const totalUnfilteredFromApi = searchPageInfo.totalCount || 0;
  const totalItemsForPagination = isFiltered ? filteredResults.length : totalUnfilteredFromApi;

  const totalPages = pageSize > 0 ? Math.ceil(totalItemsForPagination / pageSize) : 0;
  const safeCurrentPage = totalPages > 0 ? Math.min(currentPage, totalPages - 1) : 0;

  let pagedResults = [];
  const pageSliceStart = safeCurrentPage * pageSize;

  if (isFiltered) {
    pagedResults = filteredResults.slice(pageSliceStart, pageSliceStart + pageSize);
  } else {
    pagedResults = pages.get(safeCurrentPage) || [];
  }

  let startDisplay = 0;
  let endDisplay = 0;
  if (totalItemsForPagination > 0) {
    startDisplay = pageSliceStart + 1;
    endDisplay = Math.min(pageSliceStart + pageSize, totalItemsForPagination);
  }

  let resultSummary =
    totalItemsForPagination > 0
      ? `Showing ${startDisplay}-${endDisplay} of ${totalItemsForPagination} datasets${datastockSuffix}.`
      : datastockSuffix
        ? `No datasets${datastockSuffix}.`
        : 'No datasets available.';

  if (!debouncedSearch || debouncedSearch.trim().length < MIN_QUERY_LEN) {
    resultSummary = 'Enter at least two characters to search datasets.';
  } else if (searchStatus === 'loading') {
    resultSummary = 'Searching datasets...';
  }

  const canGoPrev = totalPages > 0 && safeCurrentPage > 0;
  const canGoNext = totalPages > 0 && safeCurrentPage < totalPages - 1;
  const displayPageNumber = totalPages > 0 ? safeCurrentPage + 1 : 1;

  // KEY: Only declare a page "ready" when we've EN-checked that page.
  const pageIsReady = isFiltered ? true : !!pageReady.get(safeCurrentPage);
  const hasQuery = (debouncedSearch?.trim()?.length || 0) >= MIN_QUERY_LEN;
  const searching = hasQuery && (searchStatus === 'loading' || searchStatus === 'success');

  const showSkeletons =
    searching &&
    !isFiltered &&
    (
      (STRICT_EN_NAMES ? !pageReady.get(safeCurrentPage) : !pageIsReady) ||
      (searchStatus === 'loading')
    );

  const languageSummary = useMemo(
    () => formatSelectionSummary(selectedLanguages, { fallback: 'All', transform: (l) => l.toUpperCase() }),
    [selectedLanguages]
  );
  const subtypeSummary = useMemo(
    () => formatSelectionSummary(selectedSubtypes, { fallback: 'All' }),
    [selectedSubtypes]
  );
  const geographySummary = useMemo(
    () => formatSelectionSummary(selectedGeographies, { fallback: 'All' }),
    [selectedGeographies]
  );
  const noFiltersApplied = selectedLanguages.length === 0 && selectedSubtypes.length === 0 && selectedGeographies.length === 0;

  const handleRefreshDatastock = useCallback(() => {
    setDatastockStatus('loading');
    setDatastockError(null);
    fetchLatestDatastock()
      .then((stock) => { setDatastock(stock); setDatastockStatus('success'); })
      .catch((error) => {
        console.error('Failed to refresh datastock', error);
        setDatastockError(error.message || 'Unable to refresh datastock.');
        setDatastockStatus('error');
      });
  }, [fetchLatestDatastock]);

  useEffect(() => {
    if (typeof onRegisterRefresh === "function") onRegisterRefresh(handleRefreshDatastock);
  }, [onRegisterRefresh, handleRefreshDatastock]);

  useEffect(() => {
    if (typeof onStatusChange === "function") {
      onStatusChange({
        status: datastockStatus,
        error: datastockError || null,
        meta: { datastock }
      });
    }
  }, [datastockStatus, datastockError, datastock, onStatusChange]);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="space-y-0 pt-1 sm:pt-0">
          <div className="flex flex-col gap-2" style={{ marginBottom: "10px" }}>
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
              <Input
                ref={inputRef}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search for materials, e.g. 'concrete roof tile'"
                className="pl-9 pr-12 h-9 text-xs text-gray-900"
              />
              {searchStatus === 'loading' && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 animate-spin" />
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600" style={{ marginBottom: "16px" }}>
            {SAMPLE_QUERIES.map((sample) => (
              <Badge
                key={sample}
                onClick={() => handleSelectSample(sample)}
                className={`bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200 shadow-none py-1 px-2.5 ${String(sample || "").toLowerCase() === "gypsum" ? "hidden sm:inline-flex" : ""}`}
              >
                {sample}
              </Badge>
            ))}
          </div>

          <div className="text-xs text-gray-600 mt-2" style={{ marginBottom: "12px" }}>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={availableLanguages.length === 0}
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span>Languages</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">{languageSummary}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-60">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Languages</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {availableLanguages.length === 0 ? (
                    <DropdownMenuItem className="text-xs text-gray-500" disabled>No languages detected</DropdownMenuItem>
                  ) : (
                    availableLanguages.map((lang) => (
                      <DropdownMenuCheckboxItem
                        key={lang}
                        checked={selectedLanguages.includes(lang)}
                        className="text-xs"
                        onCheckedChange={(checked) => handleLanguageToggle(lang, checked)}
                      >
                        {lang.toUpperCase()}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => { e.preventDefault(); clearLanguages(); }}
                  >
                    Clear selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700"
                  >
                    <span>Subtype</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">{subtypeSummary}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Subtype</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {availableSubtypes.map((subtype) => (
                    <DropdownMenuCheckboxItem
                      key={subtype}
                      checked={selectedSubtypes.includes(subtype)}
                      className="text-xs"
                      onCheckedChange={(checked) => handleSubtypeToggle(subtype, checked)}
                    >
                      {subtype}
                    </DropdownMenuCheckboxItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => { e.preventDefault(); clearSubtypes(); }}
                  >
                    Clear selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={availableGeographies.length === 0}
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <span>Country</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">{geographySummary}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Country</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {availableGeographies.length === 0 ? (
                    <DropdownMenuItem className="text-xs text-gray-500" disabled>No countries detected yet</DropdownMenuItem>
                  ) : (
                    availableGeographies.map((geo) => (
                      <DropdownMenuCheckboxItem
                        key={geo}
                        checked={selectedGeographies.includes(geo)}
                        className="text-xs"
                        onCheckedChange={(checked) => handleGeographyToggle(geo, checked)}
                      >
                        {geo}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => { e.preventDefault(); clearGeographies(); }}
                  >
                    Clear selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={clearAllFilters}
                disabled={noFiltersApplied}
                className="h-8 px-3 text-xs text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Clear all
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-6 text-justify sm:text-left">
            Tip: type at least two characters to trigger the OEKOBAUDAT search. Results are debounced by 350 ms to keep the API happy.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
        <Card className="bg-white border border-emerald-100 shadow self-start h-[650px] flex flex-col overflow-hidden">
          <CardHeader>
            <CardTitle className="text-gray-800">Matching materials</CardTitle>
            <CardDescription>{resultSummary}</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            {searchError && (
              <Alert variant="destructive" className="mb-4 flex-shrink-0">
                <AlertTitle>Search error</AlertTitle>
                <AlertDescription>{searchError}</AlertDescription>
              </Alert>
            )}
            <div className="flex flex-col flex-1 min-h-0">
              {searchStatus === 'idle' && (!debouncedSearch || debouncedSearch.length < 2) && (
                <div className="text-sm text-gray-400 bg-emerald-50/30 border border-dashed border-emerald-200/50 rounded-xl p-4">
                  Start typing a material name to see available datasets. Try "concrete", "timber", or "insulation".
                </div>
              )}
              {searchStatus === 'success' && allResults.length === 0 && (
                <div className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
                  <Info className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <span>No datasets matched your query. Adjust the search term or try a different keyword.</span>
                </div>
              )}
              {searchStatus === 'success' && allResults.length > 0 && filteredResults.length === 0 && (
                <div className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
                  <Info className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <span>{filterEmptyMessage}</span>
                </div>
              )}
              <ScrollArea className="flex-1 min-h-0 mt-2 pr-2">
                <div className="space-y-3">
                  {(() => {
                    if (!searching) return null;

                    const pageItems = pages.get(safeCurrentPage) || [];

                    if (STRICT_EN_NAMES && !pageReady.get(safeCurrentPage)) {
                      return Array.from({ length: pageSize }).map((_, i) => <SkeletonProcessItem key={`sk-${i}`} />);
                    }

                    if (pageItems.length === 0 && searchStatus === 'loading') {
                      return Array.from({ length: pageSize }).map((_, i) => <SkeletonProcessItem key={`sk-${i}`} />);
                    }

                    return pageItems.map((process) => (
                      <ProcessResultItem
                        key={process.uuid}
                        process={process}
                        isSelected={selectedProcess?.uuid === process.uuid}
                        onSelect={handleProcessSelect}
                        showTitleSkeleton={STRICT_EN_NAMES ? (searching && !hasEnglishName(process)) : (searching && !pageReady.get(safeCurrentPage))}
                      />
                    ));
                  })()}
                </div>
              </ScrollArea>
            </div>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800">
              <span className="font-medium text-emerald-900">{resultSummary}</span>
              {totalItemsForPagination > 0 && totalPages > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                    disabled={!canGoPrev}
                    aria-label="Previous page"
                    className="h-7 w-7 text-emerald-700 hover:text-emerald-900 disabled:text-gray-300"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-gray-500">Page {displayPageNumber} of {totalPages}</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, Math.max(totalPages - 1, 0)))}
                    disabled={!canGoNext}
                    aria-label="Next page"
                    className="h-7 w-7 text-emerald-700 hover:text-emerald-900 disabled:text-gray-300"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-emerald-100 self-start h-[650px] flex flex-col overflow-hidden">
          <CardHeader>
            <CardTitle className="text-gray-800 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-emerald-600" />
              Material impact details
            </CardTitle>
            <CardDescription>Select a dataset to inspect declared unit and environmental indicators.</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            <ScrollArea className="flex-1 min-h-0 pr-2">
              <div className="space-y-5">
                {detailStatus === 'idle' && (
                  <div className="text-sm text-gray-400 bg-emerald-50/30 border border-emerald-200/50 rounded-xl p-4">
                    Choose a material on the left to load detailed OEKOBAUDAT impact metrics.
                  </div>
                )}
                {detailStatus === 'loading' && (
                  <div className="flex items-center gap-3 text-emerald-700">
                    <Loader2 className="w-5 h-5 animate-spin" /> Fetching process details...
                  </div>
                )}
                {detailStatus === 'error' && (
                  <Alert variant="destructive">
                    <AlertTitle>Could not load dataset</AlertTitle>
                    <AlertDescription>{detailError}</AlertDescription>
                  </Alert>
                )}
                {detailStatus === 'success' && processDetail && (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 space-y-2">
                      <div className="space-y-1">
                        <h3 className="text-lg font-semibold text-emerald-900">{processDetail.name}</h3>
                        <div className="text-sm text-gray-700 space-y-1">
                          {processDetail.subtype && <p>Subtype: {processDetail.subtype}</p>}
                          {processDetail.geography && <p>Country: {processDetail.geography}</p>}
                          <p>Manufacturer / Owner: {processDetail.manufacturer || 'Not specified'}</p>
                          {validUntilLabel && <p>Valid until: {validUntilLabel}</p>}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-emerald-800">
                        {processDetail.declaredUnit && (
                          <Badge variant="outline" className="border-emerald-300 bg-white">
                            Declared unit: {declaredUnitLabel || processDetail.declaredUnit.unit || 'declared unit'}
                          </Badge>
                        )}
                        <MaterialProperties
                          materialProperties={processDetail.materialProperties}
                          declaredUnit={processDetail.declaredUnit}
                        />
                      </div>
                    </div>

                    {(hasDocumentation || (onShortlist && processDetail?.uuid)) && (
                      <div className="pt-1 flex flex-wrap items-center gap-3">
                        {hasDocumentation && (
                          <Dialog open={isDocumentationDialogOpen} onOpenChange={setDocumentationDialogOpen}>
                            <DialogTrigger asChild>
                              <Button type="button" variant="outline" size="default" className="flex items-center gap-2 border-emerald-200 bg-white/80 text-emerald-700 hover:bg-emerald-50">
                                <Info className="h-4 w-4" />
                                Show material & process details
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl">
                              <DialogHeader>
                                <DialogTitle>Material & process details</DialogTitle>
                                <DialogDescription>Documentation excerpts supplied by the OEKOBAUDAT dataset.</DialogDescription>
                              </DialogHeader>
                              <ScrollArea className="max-h-[28rem] pr-2">
                                <div className="space-y-6">
                                  {documentationSections.map((section) => (
                                    <div key={section.key} className="space-y-2">
                                      <h5 className="text-sm font-semibold uppercase tracking-wide text-emerald-800">
                                        {section.title}
                                      </h5>
                                      <p className="whitespace-pre-line text-sm text-gray-700">{section.content}</p>
                                    </div>
                                  ))}
                                </div>
                              </ScrollArea>
                            </DialogContent>
                          </Dialog>
                        )}
                        {onShortlist && processDetail?.uuid && (() => {
                          const all = Array.isArray(processDetail?.impacts) ? processDetail.impacts : [];
                          const chosen = all.find((imp) => String(imp.id) === String(selectedImpactId)) || all[0];

                          let modules = null;
                          if (chosen?.modules) {
                            if (Array.isArray(chosen.modules)) {
                              modules = {};
                              chosen.modules.forEach((m) => {
                                const key = m?.key ?? m?.module ?? m?.name;
                                if (key != null) modules[key] = Number(m?.value ?? m?.total ?? 0) || 0;
                              });
                            } else if (typeof chosen.modules === 'object') {
                              modules = chosen.modules;
                            }
                          }

                          const du = processDetail?.declaredUnit || null;
                          const gwpTotal = Number(chosen?.total) || 0;
                          const isAlready = Array.isArray(shortlist) && shortlist.some((s) => s.uuid === processDetail.uuid);

                          return isAlready ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="default"
                              className="h-9 border-red-300 text-red-700 bg-white hover:bg-red-50"
                              onClick={() => onShortlist({ uuid: processDetail.uuid, __remove: true })}
                              title="Remove from shortlist"
                            >
                              Remove from shortlist
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              size="default"
                              className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"
                              onClick={() => onShortlist({
                                uuid: processDetail.uuid,
                                name: processDetail.name,
                                declaredUnit: du ? { unit: du.unit, amount: du.amount ?? 1 } : null,
                                gwpTotal,
                                modules,
                                materialProperties: processDetail.materialProperties || null,
                                geography: processDetail.geography || null,
                                subtype: processDetail.subtype || null,
                                manufacturer: processDetail.manufacturer || null,
                              })}
                              title="Add to shortlist"
                            >
                              Shortlist
                            </Button>
                          );
                        })()}
                      </div>
                    )}

                    <div className="space-y-3">
                      <Separator className="bg-emerald-100" />
                      <h4 className="text-sm font-semibold text-emerald-700 uppercase tracking-wide">Additional impact categories</h4>
                      {Array.isArray(processDetail?.impacts) && processDetail.impacts.length > 0 && (
                        <div className="flex items-center gap-2">
                          <label htmlFor="impact-select" className="text-xs text-gray-600">Show impact</label>
                          <select
                            id="impact-select"
                            value={selectedImpactId || processDetail.impacts[0].id}
                            onChange={(e) => setSelectedImpactId(e.target.value)}
                            className="border border-emerald-200 rounded px-2 py-1 text-xs bg-white text-emerald-800"
                          >
                            {processDetail.impacts.map((imp) => (
                              <option key={imp.id} value={imp.id}>{imp.description}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="space-y-3">
                        {(() => {
                          const all = Array.isArray(processDetail?.impacts) ? processDetail.impacts : [];
                          const chosen = all.find((imp) => String(imp.id) === String(selectedImpactId)) || all[0];
                          return chosen ? (
                            <ImpactCategoryTable key={chosen.id} impact={chosen} declaredUnit={processDetail.declaredUnit} />
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OekobaudatExplorer;
